import "./index.scss";
import initForm from "../../js/forms";
import tooltipGuidance from "../../js/forms/guidance";
import {hideLoader, setLoaderState, showLoader} from "../../js/loader";
import runWorkFlow from "../../js/automation";
import {getURLParam, setLocation} from "../../js/network";
import debug from "../../js/debug";
import {login} from "../../js/security/authenticator";
import {getParentLocationId} from "../../js/api/location";
import {signupExistingLocation} from "../../js/api/signup";
import ImageLoader from "../../js/image_loader";
import Panel from "../../js/panel";
import mixer from "../../assets/images/mixer.png";
import mixerThumbnail from "../../assets/images/tn-mixer.png";

// Global parent location Id
let parentLocId = null;
const getGroupLocationId = () => {
  return parentLocId;
};

// Signup error workflows
const signupErrorsWorkflows = {
  "ERR_UNKNOWN": [
    {
      "function": setLoaderState,
      "params": ["error", ["Signup Failed", "We're experiencing some errors", "Please Try Again Later"]],
      "delay": 1500,
    },
    {
      "function": hideLoader,
      "delay": 4500,
    },
  ],
  "ERR_EMAIL_IN_USE": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "E-mail In Use", "Please Log In"]],
      "delay": 1500,
    },
    {
      "function": setLocation,
      "params": [publicPath + "/login"],
      "delay": 4500,
    },
  ],
  "ERR_LOCATION_MISSING": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "Missing Location", "Please Scan QR Code"]],
      "delay": 1500,
    },
  ],
  "ERR_FIRSTNAME_MISSING": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "First Name Missing", "Please Review Input"]],
      "delay": 1500,
    },
    {
      "function": hideLoader,
      "delay": 4500,
    },
  ],
  "ERR_LASTNAME_MISSING": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "Last Name Missing", "Please Review Input"]],
      "delay": 1500,
    },
    {
      "function": hideLoader,
      "delay": 4500,
    },
  ],
  "ERR_EMAIL_MISSING": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "E-mail Missing", "Please Review Input"]],
      "delay": 1500,
    },
    {
      "function": hideLoader,
      "delay": 4500,
    },
  ],
  "ERR_PASSWORD_MISSING": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "Password Missing", "Please Review Input"]],
      "delay": 1500,
    },
    {
      "function": hideLoader,
      "delay": 4500,
    },
  ],
  "ERR_LOCATION_MALFORMATTED": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "Location Not Recognized", "Please Review Input"]],
      "delay": 1500,
    },
    {
      "function": hideLoader,
      "delay": 4500,
    },
  ],
  "ERR_FIRSTNAME_MALFORMATTED": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "First Name Malformatted", "Please Review Input"]],
      "delay": 1500,
    },
    {
      "function": hideLoader,
      "delay": 4500,
    },
  ],
  "ERR_LASTNAME_MALFORMATTED": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "Last Name Malformatted", "Please Review Input"]],
      "delay": 1500,
    },
    {
      "function": hideLoader,
      "delay": 4500,
    },
  ],
  "ERR_EMAIL_MALFORMATTED": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "E-mail Malformatted", "Please Review Input"]],
      "delay": 1500,
    },
    {
      "function": hideLoader,
      "delay": 4500,
    },
  ],
  "ERR_PASSWORD_MALFORMATTED": [
    {
      "function": setLoaderState,
      "params": ["error", ["Sign Up Error", "Password Malformatted", "Please Review Input"]],
      "delay": 1500,
    },
    {
      "function": hideLoader,
      "delay": 4500,
    },
  ],
};

/**
 * Login after signing up
 *
 * @param form {HTMLFormElement}
 */
const loginAfterSigningUp = form => {
  login(
    form.querySelector("#emailAddress").value.trim(),
    form.querySelector("#password").value.trim(),
  ).then(() => {
    runWorkFlow([
      {
        "function": setLoaderState,
        "params": ["success", ["Login Succeeded"]],
        "delay": 1250,
      },
      {
        "function": setLoaderState,
        "params": ["setup", ["Let's Setup Your Profile!"]],
        "delay": 1250,
      },
      {
        "function": setLocation,
        "params": [publicPath + "/setup?groupLocation=" + getGroupLocationId()],
        "delay": 1750,
      },
    ]);
  })
    .catch(error => {
      debug(
        ["🐛 %csignup ~ loginAfterSigningUp", "font-weight:bold"],
        "Error when logging in after signing up 👉",
        error,
        "👈",
      );
      const messages = (!error.response || !error.response.data || !error.response.data.message ||
        !error.response.data.message.length) ? ["ERR_UNKNOWN"] : error.response.data.message;
      messages.forEach(m => {
        if (!signupErrorsWorkflows[m]) {
          return;
        }
        runWorkFlow(signupErrorsWorkflows[m]);
      });
    });
};

// Main functionality
window.addEventListener("DOMContentLoaded", () => {
  // Set up image loader for responsive blurred images
  const imageLoader = new ImageLoader([
    {
      "selector": ".signup__background_001",
      "thumbnail": mixerThumbnail,
      "fullSize": mixer,
    },
  ]);
  imageLoader.load();

  // Set up panels
  const panel = new Panel([document.getElementById("landing"), document.getElementById("signup")]);

  // Set up listeners
  document.getElementById("bttnTitleBarNavigation").addEventListener("click", e => {
    e.preventDefault();
    panel.back();
  });
  document.getElementById("landing_form").addEventListener("submit", e => e.preventDefault());
  document.getElementById("bttnLandingSignup").addEventListener("click", () => panel.next());

  // Get group from query string
  const groupKey = getURLParam("group");
  getParentLocationId(groupKey)
    .then(locId => {
      parentLocId = locId;
    })
    .catch(e => {
      debug(
        ["🐛 %csignup ~ getGroup", "font-weight:bold"],
        "query returned a non-success result 👉 ", e, " 👈 likely related " +
        "to missing location data or controller ignorance of the supplied group name",
      );
    });

  // Init form
  initForm(
    document.getElementById("signup_form"),
    {
      formControl: document.getElementById("bttnSignupSubmit"),
      fieldsValidators: {
        "firstName": {
          "required": true,
          "re": /^[a-zA-Z\s-']{2,}$/,
          "formId": ["signup_form"],
        },
        "lastName": {
          "required": true,
          "re": /^[a-zA-Z\s-']{2,}$/,
          "formId": ["signup_form"],
        },
        "emailAddress": {
          "required": true,
          "re": /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
          "guidance": tooltipGuidance["emailAddress"],
          "formId": ["signup_form"],
        },
        "password": {
          "required": true,
          "re": /^((?=.*\d)(?=.*[A-Z])(?=.*\W).{8,16})$/,
          "guidance": tooltipGuidance["password"],
          "formId": ["signup_form"],
        },
      },
      successCallback: form => {
        // Show loader
        showLoader();
        setLoaderState("animating", ["Creating Account"]);
        // Sign up
        signupExistingLocation(
          form.querySelector("#firstName").value.trim(),
          form.querySelector("#lastName").value.trim(),
          form.querySelector("#emailAddress").value.trim(),
          form.querySelector("#password").value.trim(),
          [getGroupLocationId()],
          "server",
        )
          .then(() => {
            runWorkFlow([
              {
                "function": setLoaderState,
                "params": ["success", ["Account Created"]],
                "delay": 1250,
              },
              {
                "function": setLoaderState,
                "params": ["animating", ["Logging In"]],
                "delay": 1250,
              },
              {
                "function": loginAfterSigningUp.bind(null, form),
                "params": [],
                "delay": 1250,
              },
            ]);
          })
          .catch(error => {
            debug(
              ["🐛 %csignup ~ initForm ~ successCallback", "font-weight:bold"],
              "Error when signing up 👉",
              error,
              "👈",
            );
            const messages = (!error.response || !error.response.data || !error.response.data.message ||
              !error.response.data.message.length) ? ["ERR_UNKNOWN"] : error.response.data.message;
            messages.forEach(m => {
              if (!signupErrorsWorkflows[m]) {
                return;
              }
              runWorkFlow(signupErrorsWorkflows[m]);
            });
          });
      },
    },
  );
});
